import { PRICED_SERVICE_TYPE } from 'settings/constants/service'
import { STATUS } from 'settings/constants/status'

export type ReactivationLocationRow = {
  locationId: string
  locationName?: string | null
  locActive?: boolean
  address?: { line1?: string | null; city?: string | null; state?: string | null; zipCode?: string | null } | null
}

export type ReactivationServiceRow = {
  configuredServiceId: string
  serviceName?: string | null
  serviceType?: string | null
  status?: string | null
  servicePrice?: number | null
  pricingPeriodInterval?: string | null
  pricingPeriodPer?: string | null
  recurrenceFrequency?: number | null
  recurrenceInterval?: string | null
  recurrencePer?: string | null
  methodType?: string | null
  quantity?: number | null
  newContainerCount?: number | null
  measureVolume?: number | null
  measureUnit?: string | null
  daysOfWeek?: string | null
  tags?: { id: string; tagName?: string | null; tagColor?: string | null }[] | null
  locationId?: string | null
  startDate?: string | null
  billingStartDate?: string | null
  billingEndDate?: string | null
  endDate?: string | null
  serviceEnded?: boolean | null
}

export type ReactivationLocationGroup = {
  location: ReactivationLocationRow
  isLocationDeactivated: boolean
  services: ReactivationServiceRow[]
  restorableServiceIds: string[]
}

export type ReactivationSelection = {
  locationIds: Set<string>
  serviceIds: Set<string>
}

export const isDeactivatedService = (service: ReactivationServiceRow) => service.status === STATUS.DEACTIVATED

// Usually a finished job rather than one the deactivation ended, so its end date is shown and unticking it never blocks.
export const hasEnded = (service: ReactivationServiceRow) =>
  service.serviceType !== PRICED_SERVICE_TYPE.RECURRING && Boolean(service.serviceEnded)

const byName = (a?: string | null, b?: string | null) => (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' })

const locationLabel = (location: ReactivationLocationRow) => location.address?.line1 || location.locationName

// One pass over the services: an account can have thousands of each.
const groupByLocation = (services: ReactivationServiceRow[], locations: ReactivationLocationRow[]) => {
  const byLocation = new Map<string, ReactivationServiceRow[]>()
  services.forEach(service => {
    if (!service.locationId || !(isDeactivatedService(service) || service.status === STATUS.ACTIVE)) return
    const group = byLocation.get(service.locationId)
    if (group) group.push(service)
    else byLocation.set(service.locationId, [service])
  })
  return [...locations]
    .sort((a, b) => byName(locationLabel(a), locationLabel(b)))
    .map(location => ({
      location,
      services: (byLocation.get(location.locationId) ?? []).sort((a, b) => byName(a.serviceName, b.serviceName)),
    }))
}

// Drafts are left out: deactivation never ended them, so there is nothing to bring back.
export const groupReactivatableByLocation = (
  locations: ReactivationLocationRow[],
  services: ReactivationServiceRow[]
): ReactivationLocationGroup[] =>
  groupByLocation(services, locations)
    .map(({ location, services: locationServices }) => ({
      location,
      isLocationDeactivated: location.locActive === false,
      services: locationServices,
      restorableServiceIds: locationServices.filter(isDeactivatedService).map(service => service.configuredServiceId),
    }))
    .filter(group => group.isLocationDeactivated || group.services.some(isDeactivatedService))

export const selectAllReactivatable = (groups: ReactivationLocationGroup[]): ReactivationSelection => ({
  locationIds: new Set(groups.filter(group => group.isLocationDeactivated).map(group => group.location.locationId)),
  serviceIds: new Set(groups.flatMap(group => group.restorableServiceIds)),
})

export const emptySelection = (): ReactivationSelection => ({ locationIds: new Set(), serviceIds: new Set() })

// A saved schedule keeps only what can still come back: a location or service reactivated since drops out.
export const selectSavedReactivatable = (
  groups: ReactivationLocationGroup[],
  saved: { locationIds: string[]; configuredServiceIds: string[] }
): ReactivationSelection => {
  const savedLocationIds = new Set(saved.locationIds)
  const savedServiceIds = new Set(saved.configuredServiceIds)
  const all = selectAllReactivatable(groups)
  return {
    locationIds: new Set(Array.from(all.locationIds).filter(id => savedLocationIds.has(id))),
    serviceIds: new Set(Array.from(all.serviceIds).filter(id => savedServiceIds.has(id))),
  }
}

// Unchecking a location drops its services (none can come back on a deactivated location); checking it brings back the location alone.
export const toggleLocation = (selection: ReactivationSelection, group: ReactivationLocationGroup): ReactivationSelection => {
  const locationIds = new Set(selection.locationIds)
  const serviceIds = new Set(selection.serviceIds)
  if (locationIds.has(group.location.locationId)) {
    locationIds.delete(group.location.locationId)
    group.restorableServiceIds.forEach(id => serviceIds.delete(id))
  } else {
    locationIds.add(group.location.locationId)
  }
  return { locationIds, serviceIds }
}

export const toggleService = (
  selection: ReactivationSelection,
  group: ReactivationLocationGroup,
  serviceId: string
): ReactivationSelection => {
  const locationIds = new Set(selection.locationIds)
  const serviceIds = new Set(selection.serviceIds)
  if (serviceIds.has(serviceId)) {
    serviceIds.delete(serviceId)
  } else {
    serviceIds.add(serviceId)
    if (group.isLocationDeactivated) locationIds.add(group.location.locationId)
  }
  return { locationIds, serviceIds }
}
